<template>
  <div>
    <!-- Sidebar backdrop (mobile only) -->
    <div
      class="fixed inset-0 z-40 bg-slate-900 bg-opacity-30 transition-opacity duration-200 lg:z-auto lg:hidden"
      :class="sidebarOpen ? 'opacity-100' : 'pointer-events-none opacity-0'"
      aria-hidden="true"
    ></div>

    <!-- Sidebar -->
    <div
      id="sidebar"
      ref="sidebar"
      class="no-scrollbar absolute left-0 top-0 z-40 flex h-screen w-64 shrink-0 flex-col overflow-y-scroll bg-slate-800 p-4 transition-all duration-200 ease-in-out lg:static lg:left-auto lg:top-auto lg:w-20 lg:translate-x-0 lg:overflow-y-auto lg:sidebar-expanded:!w-64 xl:!w-64"
      :class="sidebarOpen ? 'translate-x-0' : '-translate-x-64'"
    >
      <!-- Sidebar header -->
      <div class="mb-6 flex justify-between pr-3 sm:px-2">
        <!-- Close button -->
        <button
          ref="trigger"
          class="text-slate-500 hover:text-slate-400 lg:hidden"
          @click.stop="$emit('close-sidebar')"
          aria-controls="sidebar"
          :aria-expanded="sidebarOpen"
        >
          <span class="sr-only">Close sidebar</span>
          <svg class="h-6 w-6 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.7 18.7l1.4-1.4L7.8 13H20v-2H7.8l4.3-4.3-1.4-1.4L4 12z" />
          </svg>
        </button>
        <!-- Logo -->
        <router-link class="block w-full" to="/">
          <div class="flex w-full items-center justify-start">
            <img src="@/assets/images/logo.png" width="120" alt="logo" />
          </div>
        </router-link>
      </div>

      <!-- Links -->
      <div class="space-y-8">
        <!-- Pages group -->
        <div>
          <h3 class="pl-3 text-xs font-semibold uppercase text-slate-500">
            <span
              class="hidden w-6 text-center lg:block lg:sidebar-expanded:hidden xl:hidden"
              aria-hidden="true"
              >•••</span
            >
            <span class="lg:hidden lg:sidebar-expanded:block xl:block">Menu</span>
          </h3>

          <!-- Sidebar items -->
          <ul class="mt-3">
            <!-- Home -->
            <router-link to="/" custom v-slot="{ href, navigate, isExactActive }">
              <li
                class="mb-0.5 rounded-sm px-3 py-2 last:mb-0"
                :class="isExactActive && 'bg-slate-900'"
              >
                <a
                  class="block truncate text-slate-200 transition duration-150"
                  :class="isExactActive ? 'hover:text-slate-200' : 'hover:text-white'"
                  :href="href"
                  @click="navigate"
                >
                  <div class="flex items-center justify-between">
                    <div class="flex grow items-center">
                      <n-icon size="20" class="mr-3">
                        <HomeOutline />
                      </n-icon>
                      <span
                        class="text-sm font-medium duration-200 lg:opacity-0 lg:sidebar-expanded:opacity-100 xl:opacity-100"
                        >Home</span
                      >
                    </div>
                  </div>
                </a>
              </li>
            </router-link>

            <!-- Users -->
            <router-link to="/users/" custom v-slot="{ href, navigate, isExactActive }">
              <li
                class="mb-0.5 rounded-sm px-3 py-2 last:mb-0"
                :class="isExactActive && 'bg-slate-900'"
              >
                <a
                  class="block truncate text-slate-200 transition duration-150"
                  :class="isExactActive ? 'hover:text-slate-200' : 'hover:text-white'"
                  :href="href"
                  @click="navigate"
                >
                  <div class="flex items-center justify-between">
                    <div class="flex grow items-center">
                      <n-icon size="20" class="mr-3">
                        <PeopleOutline />
                      </n-icon>
                      <span
                        class="text-sm font-medium duration-200 lg:opacity-0 lg:sidebar-expanded:opacity-100 xl:opacity-100"
                        >Users</span
                      >
                    </div>
                  </div>
                </a>
              </li>
            </router-link>

            <!-- Forge -->
            <router-link to="/forge/" custom v-slot="{ href, navigate, isExactActive }">
              <li
                class="mb-0.5 rounded-sm px-3 py-2 last:mb-0"
                :class="isExactActive && 'bg-slate-900'"
              >
                <a
                  class="block truncate text-slate-200 transition duration-150"
                  :class="isExactActive ? 'hover:text-slate-200' : 'hover:text-white'"
                  :href="href"
                  @click="navigate"
                >
                  <div class="flex items-center justify-between">
                    <div class="flex grow items-center">
                      <n-icon size="20" class="mr-3">
                        <CubeOutline />
                      </n-icon>
                      <span
                        class="text-sm font-medium duration-200 lg:opacity-0 lg:sidebar-expanded:opacity-100 xl:opacity-100"
                        >Forge</span
                      >
                    </div>
                  </div>
                </a>
              </li>
            </router-link>

            <!-- Reports -->
            <router-link to="/reports/" custom v-slot="{ href, navigate, isExactActive }">
              <li
                class="mb-0.5 rounded-sm px-3 py-2 last:mb-0"
                :class="isExactActive && 'bg-slate-900'"
              >
                <a
                  class="block truncate text-slate-200 transition duration-150"
                  :class="isExactActive ? 'hover:text-slate-200' : 'hover:text-white'"
                  :href="href"
                  @click="navigate"
                >
                  <div class="flex items-center justify-between">
                    <div class="flex grow items-center">
                      <n-icon size="20" class="mr-3">
                        <DocumentTextOutline />
                      </n-icon>
                      <span
                        class="text-sm font-medium duration-200 lg:opacity-0 lg:sidebar-expanded:opacity-100 xl:opacity-100"
                        >Отчеты</span
                      >
                    </div>
                  </div>
                </a>
              </li>
            </router-link>
          </ul>
        </div>
      </div>

      <!-- Expand / collapse button -->
      <div class="mt-auto hidden justify-end pt-3 lg:inline-flex xl:hidden">
        <div class="px-3 py-2">
          <button @click.prevent="sidebarExpanded = !sidebarExpanded">
            <span class="sr-only">Expand / collapse sidebar</span>
            <svg class="h-6 w-6 fill-current sidebar-expanded:rotate-180" viewBox="0 0 24 24">
              <path
                class="text-slate-400"
                d="M19.586 11l-5-5L16 4.586 23.414 12 16 19.414 14.586 18l5-5H7v-2z"
              />
              <path class="text-slate-600" d="M3 23H1V1h2z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch } from "vue";
import { NIcon } from "naive-ui";
import { CubeOutline, PeopleOutline, HomeOutline, DocumentTextOutline } from "@vicons/ionicons5";

export default {
  name: "SidebarView",
  props: ["sidebarOpen"],
  components: {
    NIcon,
    CubeOutline,
    PeopleOutline,
    HomeOutline,
    DocumentTextOutline,
  },
  setup(props, { emit }) {
    const trigger = ref(null);
    const sidebar = ref(null);
    const sidebarExpanded = ref(false);

    // close on click outside
    const clickHandler = ({ target }) => {
      if (!sidebar.value || !trigger.value) return;
      if (!props.sidebarOpen || sidebar.value.contains(target) || trigger.value.contains(target))
        return;
      emit("close-sidebar");
    };

    // close if the esc key is pressed
    const keyHandler = ({ keyCode }) => {
      if (!props.sidebarOpen || keyCode !== 27) return;
      emit("close-sidebar");
    };

    onMounted(() => {
      document.addEventListener("click", clickHandler);
      document.addEventListener("keydown", keyHandler);
    });

    onUnmounted(() => {
      document.removeEventListener("click", clickHandler);
      document.removeEventListener("keydown", keyHandler);
    });

    watch(sidebarExpanded, () => {
      if (sidebarExpanded.value) {
        document.querySelector("body")?.classList.add("sidebar-expanded");
      } else {
        document.querySelector("body")?.classList.remove("sidebar-expanded");
      }
    });

    return {
      trigger,
      sidebar,
      sidebarExpanded,
    };
  },
};
</script>
